import { ButtonList } from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import type { Conversation } from 'lib-shared-types-api';
import { MouseEventHandler, useCallback } from 'react';

import { conversationsApi } from 'apis/conversations';
import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ConversationsTableRowMenuItemBaseProps } from '../conversationsTableRowMenuItem.types';

type ConversationsTableRowMenuCloseProps = {
  /** The conversation to close. */
  conversation: Conversation;
} & ConversationsTableRowMenuItemBaseProps;

/** Represents the component for closing the conversation. */
export const ConversationsTableRowMenuClose = ({
  conversation,
  toggleDropdown,
}: ConversationsTableRowMenuCloseProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { id: csid, live_assesment } = conversation;
  const isEndUserConnected = !!live_assesment?.is_end_user_connected;
  const label = isEndUserConnected ? 'End conversation' : 'Close conversation';

  /** Handles closing the conversation. */
  const handleClick = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(async () => {
    toggleDropdown();

    const { isCancelled, isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: isEndUserConnected
              ? 'Are you sure you want end a live conversation?'
              : 'Are you sure you want to close a conversation?',
            description: isEndUserConnected
              ? 'This action will end a live conversation with a customer.'
              : `This action will close conversation #${csid}.`,
            confirm: {
              variant: 'danger',
              label: `Yes, ${
                isEndUserConnected ? 'end' : 'close'
              } conversation`,
              async: true,
              asyncLabel: `${
                isEndUserConnected ? 'Ending' : 'Closing'
              } conversation...`,
            },
          },
        },
      }),
    );

    if (isCancelled) {
      ampli.modalConversationCloseCancelClicked();
    }

    if (isConfirmed) {
      ampli.modalConversationCloseConfirmClicked({ csid });

      confirm(async () => {
        const response = await dispatch(
          conversationsApi.endpoints.updateConversationClose.initiate({ csid }),
        );

        if (isResponseWithData(response)) {
          ampli.conversationClosed({
            location: 'conversationsOverview',
            channel: conversation.initial_channel,
            language:
              'language' in conversation.attributes
                ? conversation.attributes.language
                : undefined,
            direction: conversation.direction,
          });

          notify({
            title: `Conversation ${isEndUserConnected ? 'ended' : 'closed'}`,
            text: `Conversation #${csid} was successfully ${
              isEndUserConnected ? 'ended' : 'closed'
            }`,
            icon: 'info',
            status: 'success',
            time: 3000,
          });
        } else {
          Sentry.captureException(response.error, {
            tags: {
              team: SentryTeam.Conversations,
            },
          });

          notify({
            title: `Error while ${
              isEndUserConnected ? 'ending' : 'closing'
            } conversation`,
            text: `${
              isEndUserConnected ? 'Ending' : 'Closing'
            } conversation ${csid} was not successful.`,
            icon: 'exclamation',
            status: 'warning',
          });
        }
      });
    }
  }, [
    isEndUserConnected,
    csid,
    conversation.initial_channel,
    conversation.attributes,
    conversation.direction,
    dispatch,
    toggleDropdown,
    notify,
  ]);

  return <ButtonList.Item onClick={handleClick}>{label}</ButtonList.Item>;
};
